'use client';
import { forwardRef } from 'react';
import { motion } from 'framer-motion';
import { fadeUp, staggerContainer } from '@/lib/motion';
import { cn } from '@/lib/utils';

export interface SectionProps extends React.HTMLAttributes<HTMLElement> {
  eyebrow?: string;
  heading?: React.ReactNode;
  description?: React.ReactNode;
  align?: 'left' | 'center';
  tone?: 'default' | 'cream' | 'navy' | 'transparent';
  /** Vertical padding. */
  spacing?: 'sm' | 'md' | 'lg' | 'xl';
  /** Render header inline (no animation). */
  staticHeader?: boolean;
  /** Hide built-in container — use when section needs full bleed. */
  noContainer?: boolean;
  headerClassName?: string;
}

const toneClasses: Record<NonNullable<SectionProps['tone']>, string> = {
  default: 'bg-surface text-ink',
  cream: 'bg-surface-cream text-ink',
  navy: 'bg-primary-900 text-surface-light',
  transparent: '',
};

const spacingClasses: Record<NonNullable<SectionProps['spacing']>, string> = {
  sm: 'py-12 md:py-16',
  md: 'py-16 md:py-24',
  lg: 'py-20 md:py-32',
  xl: 'py-28 md:py-40',
};

/**
 * Editorial section primitive. Wraps content with consistent vertical rhythm,
 * an optional eyebrow + heading block, and an alternating tone background.
 */
export const Section = forwardRef<HTMLElement, SectionProps>(
  (
    {
      eyebrow,
      heading,
      description,
      align = 'left',
      tone = 'default',
      spacing = 'md',
      staticHeader = false,
      noContainer = false,
      headerClassName,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const hasHeader = !!eyebrow || !!heading || !!description;
    const Wrapper = noContainer ? 'div' : 'div';
    const wrapperClass = noContainer ? '' : 'container-page';
    const onDarkTone = tone === 'navy';

    const Header = hasHeader && (
      <div
        className={cn(
          'mb-10 md:mb-14 max-w-3xl',
          align === 'center' && 'mx-auto text-center',
          headerClassName,
        )}
      >
        {eyebrow && (
          <span
            className={cn(
              'eyebrow inline-block mb-4',
              onDarkTone ? 'text-accent' : 'text-accent',
            )}
          >
            {eyebrow}
          </span>
        )}
        {heading && (
          <h2
            className={cn(
              'font-display text-h2 leading-tight tracking-tight',
              onDarkTone ? 'text-surface-light' : 'text-ink',
            )}
          >
            {heading}
          </h2>
        )}
        {description && (
          <p
            className={cn(
              'mt-4 text-lg leading-relaxed',
              onDarkTone ? 'text-surface-light/70' : 'text-ink-secondary',
            )}
          >
            {description}
          </p>
        )}
      </div>
    );

    return (
      <section
        ref={ref}
        className={cn(toneClasses[tone], spacingClasses[spacing], className)}
        {...props}
      >
        <Wrapper className={wrapperClass}>
          {hasHeader && (
            staticHeader ? (
              Header
            ) : (
              <motion.div
                variants={staggerContainer(0.08, 0)}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, margin: '-80px' }}
              >
                <motion.div variants={fadeUp}>{Header}</motion.div>
              </motion.div>
            )
          )}
          {children}
        </Wrapper>
      </section>
    );
  },
);
Section.displayName = 'Section';
